/**
 * Mock diagnosis result for the vlan-cleanup-v3 workflow against
 * marriott-nyc-21-prod. Mix of compliant + drift + missing settings so the
 * UI exercises every state pill.
 */
import type { ProvisioningDiagnosis } from "@/api/schemas/provisioning";

export const MOCK_DIAGNOSES: Record<string, ProvisioningDiagnosis> = {
  "vlan-cleanup-v3": {
    workflowSlug: "vlan-cleanup-v3",
    workflowName: "Standard guest VLAN cleanup",
    networkId: "L_111111111111111111",
    networkName: "marriott-nyc-21-prod",
    score: 64,
    computedAt: new Date().toISOString(),
    settings: [
      {
        id: "s-1",
        path: "vlans.51.subnet",
        name: "VLAN 51 · subnet",
        category: "VLAN",
        state: "drift",
        current: "10.21.50.0/24",
        desired: "10.21.51.0/24",
        note: "Subnet stayed on legacy VLAN 50 even though VLAN 51 was created.",
        willPush: true,
      },
      {
        id: "s-2",
        path: "vlans.51.applianceIp",
        name: "VLAN 51 · appliance IP",
        category: "VLAN",
        state: "drift",
        current: "10.21.50.1",
        desired: "10.21.51.1",
        note: null,
        willPush: true,
      },
      {
        id: "s-3",
        path: "vlans.51.dhcpHandling",
        name: "VLAN 51 · DHCP handling",
        category: "VLAN",
        state: "compliant",
        current: "Run a DHCP server",
        desired: "Run a DHCP server",
        note: null,
        willPush: false,
      },
      {
        id: "s-4",
        path: "vlans.50",
        name: "Legacy VLAN 50 (should be removed)",
        category: "VLAN",
        state: "drift",
        current: { id: 50, name: "guest-old", subnet: "10.21.50.0/24" },
        desired: null,
        note: "Workflow removes the deprecated guest VLAN once VLAN 51 is fully promoted.",
        willPush: true,
      },
      {
        id: "s-5",
        path: "ports.range.13-19.accessVlan",
        name: "Access VLAN on ports 13–19",
        category: "Switch ports",
        state: "drift",
        current: 50,
        desired: 51,
        note: "All 7 ports still on VLAN 50.",
        willPush: true,
      },
      {
        id: "s-6",
        path: "ports.range.13-19.bpduGuard",
        name: "BPDU guard on ports 13–19",
        category: "Switch ports",
        state: "compliant",
        current: true,
        desired: true,
        note: null,
        willPush: false,
      },
      {
        id: "s-7",
        path: "acls.102",
        name: "ACL 102 · guest subnet allow",
        category: "Security",
        state: "missing",
        current: null,
        desired: { id: 102, action: "permit", source: "10.21.51.0/24", destination: "any" },
        note: "ACL doesn't exist yet; will be created with the new VLAN subnet.",
        willPush: true,
      },
      {
        id: "s-8",
        path: "acls.legacy.102",
        name: "ACL 102 · legacy subnet allow",
        category: "Security",
        state: "drift",
        current: { id: 102, action: "permit", source: "10.21.50.0/24", destination: "any" },
        desired: null,
        note: "Old ACL on the deprecated subnet — removed when the VLAN is dropped.",
        willPush: true,
      },
      {
        id: "s-9",
        path: "ssids.0.tags",
        name: "Guest SSID · tag list",
        category: "Wireless",
        state: "compliant",
        current: ["floor-3", "guest"],
        desired: ["floor-3", "guest"],
        note: null,
        willPush: false,
      },
      {
        id: "s-10",
        path: "monitoring.snmp",
        name: "SNMP polling target",
        category: "Monitoring",
        state: "skipped",
        current: "172.20.30.53",
        desired: "172.20.30.53",
        note: "Skipped — not in the workflow scope.",
        willPush: false,
      },
    ],
  },
};

/**
 * Best-effort fallback for a workflow slug we don't have a fixture for —
 * returns a synthesized diagnosis so the page is still demoable for any slug.
 */
export function buildPlaceholderDiagnosis(slug: string): ProvisioningDiagnosis {
  return {
    workflowSlug: slug,
    workflowName: slug.replace(/-/g, " "),
    networkId: "L_111111111111111111",
    networkName: "marriott-nyc-21-prod",
    score: 100,
    computedAt: new Date().toISOString(),
    settings: [],
  };
}
